/* global React */
import { useEffect, useState } from 'react'
import { Box, lighten, useTheme } from '@mui/material'
import { classNames, getThemeColor, isNum, isObj } from 'lib/util'
import { useMemoRef, useStyles } from 'platform/react/hook'

// eslint-disable-next-line no-unused-vars
const styles = (theme, { background, color, padding, rotate }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    backgroundColor: (background && getThemeColor(theme, background)) || (color
      ? lighten(getThemeColor(theme, color), 0.9)
      : theme.palette.background.selected),
    borderRadius: '50%',
    padding: ((padding || padding === 0) && isNum(padding) && theme.spacing(padding))
      || padding
      || theme.spacing(1.5),
  },
  icon: {
    ...rotate && {
      transform: `rotate(${rotate}deg)`,
    },
    maskSize: 'contain',
    maskPosition: 'center',
    maskRepeat: 'no-repeat',
  },
  ...theme.custom.icon,
})

/**
 * @typedef {Object} IconType
 * @property {string} name              name of the svg icon
 * @property {?string} [color]          optional color
 * @property {?string} [background]     optional background color
 * @property {boolean} [rounded]        use `shape={'circular'} instead`
 * @property {number|string} [size=24]  size of the icon
 * @property {?number} [padding]        padding around the icon
 * @property {?number} [rotate]         angle to rotate the icon
 */

/**
 * Icon Component
 *
 * TODO: Implement `shape`
 *
 * Displays custom SVG icon.
 * https://caniuse.com/?search=mask
 *
 * @param {Object} p
 * @param {React.Ref<JSX.Element>} p.forwardedRef   ref for the element
 * @param {typeof IconType.name} p.name             name of the icon
 * @param {typeof IconType.size} [p.size=24]        size of the icon
 * @param {typeof IconType.color} p.color           color of the icon
 * @param {typeof IconType.background} p.background background color for the icon
 * @param {typeof IconType.rounded} [p.rounded]     use `shape={'circular'}` instead
 * @param {typeof IconType.padding} [p.iconPadding] padding of the icon
 * @param {?number} p.rotate                        angle to rotate the icon
 * @param {string} p.className                      external styling
 * @param {IconType|String} [source]                higher priority alternative configuration
 * @returns {null|JSX.Element}
 */
const Icon = ({
  forwardedRef,
  className,
  iconPadding,
  source,
  ...props
}) => {
  const sourceObj = isObj(source) ? source : {}
  const {
    name: iconName = source || 'generic',
    size = 24,
    color,
    background,
    rounded,
    rotate,
    padding = iconPadding,
  } = { ...props, ...sourceObj }

  const [src, setSrc] = useState(null)
  const classes = useStyles(styles, { background, color, padding, rotate })()
  const theme = useTheme()

  useEffect(() => {
    let active = true

    iconName
      && active
      && setSrc(`assets/icon/${iconName || 'generic'}.svg`)

    return () => { active = false }
  }, [iconName])

  return !src ? null : (
    <>
      {rounded ? (
        <Box className={classes.root}>
          <Box
            ref={forwardedRef}
            className={classNames(classes.icon, className)}
            sx={{
              width: size,
              height: size,
              maskImage: `url(${src})`,
              backgroundColor: (color && getThemeColor(theme, color, true))
                || theme.palette.black.main,
            }}
          />
        </Box>
      ) : (
        <Box
          ref={forwardedRef}
          className={classNames(classes.icon, className)}
          sx={{
            width: size,
            height: size,
            maskImage: `url(${src})`,
            backgroundColor: (color && getThemeColor(theme, color, true))
              || theme.palette.black.main,
          }}
        />
      )}
    </>
  )
}

export const FileIcon = ({ ...props }) => Icon({
  ...props,
  name: `file-${props.name}`,
})

export default useMemoRef(Icon, props => [
  props.source,
  props.name,
  props.color,
  props.background,
  props.rotate,
  props.className,
])
